<template>
  <BundleSignupShell>
    <p class="text-center text-xs font-bold tracking-widest uppercase text-gray-400 mb-1.5">
      Step 1 of 3 · Your details
    </p>
    <h1 class="text-center text-xl font-medium text-gray-900 mb-4">
      Start Your Complete Coaching Business
    </h1>

    <form novalidate @submit.prevent="submit">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
        <div class="mb-2.5">
          <label :class="labelClass">First Name</label>
          <input v-model="form.first_name" type="text" :class="inputClass" placeholder="John" />
        </div>
        <div class="mb-2.5">
          <label :class="labelClass">Last Name</label>
          <input v-model="form.last_name" type="text" :class="inputClass" placeholder="Doe" />
        </div>
      </div>

      <div class="mb-2.5">
        <label :class="labelClass">Email</label>
        <input v-model="form.email" type="email" :class="inputClass" placeholder="john@example.com" />
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
        <div class="mb-2.5">
          <label :class="labelClass">Business Name</label>
          <input v-model="form.business_name" type="text" :class="inputClass" placeholder="Acme Coaching" />
        </div>
        <div class="mb-2.5">
          <label :class="labelClass">Phone</label>
          <input v-model="form.phone" type="tel" :class="inputClass" placeholder="(555) 123-4567" />
        </div>
      </div>

      <div class="flex items-center gap-2.5 text-xs font-bold tracking-wider uppercase text-gray-400 mt-3 mb-2.5">
        Business address
        <span class="flex-1 h-px bg-gray-100"></span>
      </div>

      <div class="mb-2.5">
        <label :class="labelClass">Street Address</label>
        <input v-model="form.street" type="text" :class="inputClass" placeholder="123 Main St" />
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
        <div class="mb-2.5">
          <label :class="labelClass">City</label>
          <input v-model="form.city" type="text" :class="inputClass" placeholder="Austin" />
        </div>
        <div class="mb-2.5">
          <label :class="labelClass">Country</label>
          <select v-model="form.country" :class="inputClass">
            <option value="" disabled>Select country…</option>
            <option v-for="name in countries" :key="name" :value="name">
              {{ name }}
            </option>
          </select>
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
        <div class="mb-2.5">
          <label :class="labelClass">State / Province</label>
          <input v-model="form.state" type="text" :class="inputClass" placeholder="Texas" />
        </div>
        <div class="mb-2.5">
          <label :class="labelClass">ZIP / Postal Code</label>
          <input v-model="form.zip" type="text" :class="inputClass" placeholder="78701" />
        </div>
      </div>

      <button type="submit" :class="ctaClass">Continue to Billing</button>

      <p class="text-center text-xs text-gray-600 mt-3 leading-relaxed">
        By joining, you agree to the
        <a href="https://coachcatalyst.com/terms-of-use" target="_blank" class="text-cc-green-500">terms of service</a>
        and
        <a href="https://coachcatalyst.com/privacy-policy" target="_blank" class="text-cc-green-500">privacy policy</a>
      </p>
    </form>
  </BundleSignupShell>
</template>

<script>
  import { mapState } from 'vuex';
  import ApiUrls from '../../../api-url';
  import AuthenticationMixin from '../../../authentication-mixin';
  import ValidationMixin from '../../../validation-mixin';
  import getters from '../../../stores/login-store/getters/login-getters';
  import { trackEvent } from '../../../services/analytics';
  import { countries as countriesData } from 'countries-list';
  import BundleSignupShell from './bundle-signup-shell.vue';

  const COUNTRY_NAMES = Object.values(countriesData)
    .map((c) => c.name)
    .sort((a, b) => a.localeCompare(b));

  export default {
    name: 'BundleSignupDetails',
    mixins: [ApiUrls, AuthenticationMixin, ValidationMixin],
    components: { BundleSignupShell },
    data() {
      return {
        countries: COUNTRY_NAMES,
        labelClass: 'block text-sm font-semibold text-gray-900 mb-1',
        inputClass:
          'w-full box-border text-sm text-gray-900 px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-cc-green-500 focus:ring-2 focus:ring-cc-green-500 focus:ring-opacity-20',
        ctaClass:
          'w-full flex items-center justify-center gap-2 mt-4 px-4 py-2.5 text-base font-semibold text-white bg-cc-green-500 rounded-md hover:brightness-105 transition',
        form: {
          first_name: '',
          last_name: '',
          email: '',
          business_name: '',
          phone: '',
          street: '',
          city: '',
          state: '',
          zip: '',
          country: '',
        },
      };
    },
    computed: {
      ...mapState(['userForm']),
    },
    created() {
      if (this.userForm && Object.keys(this.userForm).length) {
        this.form = { ...this.form, ...this.userForm };
      }
      trackEvent('bundle_signup_viewed', {
        utm_source: this.$route.query.utm_source || this.$route.query.source || null,
      });
    },
    methods: {
      isValid() {
        const required = {
          first_name: 'First name required.',
          last_name: 'Last name required.',
          business_name: 'Business name required.',
          street: 'Street address required.',
          city: 'City required.',
          state: 'State / province required.',
          zip: 'ZIP / postal code required.',
          country: 'Country required.',
        };
        for (const key of Object.keys(required)) {
          if (!this.form[key] || !this.form[key].toString().trim()) {
            this.$toast.show(required[key], this.toasted_options.error);
            return false;
          }
        }
        if (!this.validEmail(this.form.email)) {
          this.$toast.show('Invalid email.', this.toasted_options.error);
          return false;
        }
        return true;
      },
      submit() {
        if (!this.isValid()) return;
        this.$store.commit('setUserForm', { ...this.form });
        trackEvent('bundle_signup_info_completed', { email: this.form.email });
        this.$store.commit('setSelectedStep', getters.BUNDLE_SIGNUP_BILLING());
      },
    },
  };
</script>
